import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Article } from "./useArticles";

export function useArticle(slug: string | undefined) {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArticle = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaError } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (supaError) throw supaError;
      // Normalize null fields to prevent runtime crashes
      const normalized = data ? {
        ...data,
        tags: Array.isArray(data.tags) ? data.tags : [],
        content: data.content || "",
        additional_images: Array.isArray(data.additional_images) ? data.additional_images : [],
        image_captions: Array.isArray(data.image_captions) ? data.image_captions : [],
      } : null;
      setArticle(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar el artículo");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Incrementar el contador de lecturas una vez por sesión y por artículo.
  useEffect(() => {
    if (!slug) return;
    const key = `viewed_${slug}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Si sessionStorage no está disponible, se contará igualmente.
    }
    supabase.rpc("increment_article_views", { p_slug: slug }).then(
      () => {},
      () => {}, // Silenciar errores: la lectura no debe romper la página.
    );
  }, [slug]);

  useEffect(() => {
    fetchArticle();

    if (!slug) return;

    const channel = supabase
      .channel(`article-${slug}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "articles", filter: `slug=eq.${slug}` },
        () => {
          fetchArticle();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [slug, fetchArticle]);

  return { article, loading, error, refetch: fetchArticle };
}